import {Suspense} from 'react'
import {useLazyLoadQuery} from 'react-relay'
import {graphql} from 'relay-runtime'
import {Banner} from '@primer/react/experimental'
import {PAGE_SIZE, RelationshipsListAllView, RelationshipsListViewLoadingSkeleton} from './RelationshipsListAllView'
import type {LazyRelationshipsBlockingListViewQuery} from './__generated__/LazyRelationshipsBlockingListViewQuery.graphql'
import {LABELS} from '../../../constants/labels'

export const LazyRelationshipsBlockingListViewQueryGraphQL = graphql`
  query LazyRelationshipsBlockingListViewQuery($id: ID!) {
    issue: node(id: $id) {
      ... on Issue {
        repository {
          nameWithOwner
        }
        blocking(first: 100) {
          nodes {
            ...RelationshipsListAllViewItemFragments
          }
        }
      }
    }
  }
`

export type LazyRelationshipsBlockingListViewProps = {
  itemId: string
  totalItems?: number
}
export function LazyRelationshipsBlockingListView({itemId, totalItems = -1}: LazyRelationshipsBlockingListViewProps) {
  // TODO: when issueDependenciesSummary totals are available, remove the default -1 behavior altogether
  if (totalItems === -1) totalItems = Math.floor(PAGE_SIZE / 2)
  const loadingSkeletonComponent = <RelationshipsListViewLoadingSkeleton totalItems={totalItems} />

  return (
    <Suspense fallback={loadingSkeletonComponent}>
      <RelationshipsBlockingListView itemId={itemId} />
    </Suspense>
  )
}

type RelationshipsBlockingListViewProps = {itemId: string}
function RelationshipsBlockingListView({itemId}: RelationshipsBlockingListViewProps) {
  const queryData = useLazyLoadQuery<LazyRelationshipsBlockingListViewQuery>(
    LazyRelationshipsBlockingListViewQueryGraphQL,
    {id: itemId},
  )
  const emptyBanner = (
    <Banner variant="info">
      <Banner.Title>{LABELS.emptyBlockingList}</Banner.Title>
    </Banner>
  )

  const {issue} = queryData
  if (!issue) return emptyBanner

  const repository = issue.repository
  if (!repository) return emptyBanner

  const issueFragments = issue.blocking?.nodes?.filter(issueFragment => !!issueFragment)
  if (!issueFragments || issueFragments.length === 0) return emptyBanner

  return (
    <RelationshipsListAllView
      accessibleTitle={LABELS.relationNames.blockedByIssues}
      repositoryNameWithOwner={repository.nameWithOwner}
      issueFragments={issueFragments}
    />
  )
}

try{ LazyRelationshipsBlockingListView.displayName ||= 'LazyRelationshipsBlockingListView' } catch {}
try{ RelationshipsBlockingListView.displayName ||= 'RelationshipsBlockingListView' } catch {}